import { FormProvider, useForm } from 'react-hook-form'
import { getShopLocale, prefixLocale, replaceLocale } from '@helpers/env'
import { useEffect, useState } from 'react'
import { SetSelectionArgs } from '@components/ui/formElements/Dropdown/List'
import SelectField from '@components/account/pages/details/drawer/shared/SelectField'
import styles from './styles.module.scss'
import { ReactComponent as UKFlagIcon } from '@svgs/flag/uk-flag.svg'
import { ReactComponent as EUFlagIcon } from '@svgs/flag/eu-flag.svg'
import { ReactComponent as USFlagIcon } from '@svgs/flag/us-flag.svg'
import { setCookie } from '@helpers/session'
import { COOKIE_EXPIRE_1_MONTH } from '@consts/index'
import { analytics } from '@helpers/analytics'
import useIsReactClient from '@helpers/hooks/useIsReactClient'

const COUNTRIES = [
  {
    label: 'United Kingdom',
    value: 'gb',
    flag: <UKFlagIcon />
  },
  {
    label: 'USA',
    value: 'us',
    flag: <USFlagIcon />
  },
  {
    label: 'Europe',
    value: 'eu',
    flag: <EUFlagIcon />
  }
] as const

type CountryLocale = (typeof COUNTRIES)[number]['value']

function getInitialLocale() {
  return (prefixLocale() || 'gb').replace(/\//, '') as CountryLocale
}

export default function CountrySwitcher() {
  const [selectedCountry, setSelectedCountry] = useState<CountryLocale>('gb')
  const methods = useForm()
  const isReactClient = useIsReactClient()

  const defaultCountry =
    COUNTRIES.find(country => country.value === getShopLocale())?.label ||
    'Select Country'

  const handleSelectCountry = (args: SetSelectionArgs) => {
    const newValue = args.selectedCountry as CountryLocale

    if (selectedCountry !== newValue) {
      setSelectedCountry(newValue)
      setCookie(
        'th_chosen',
        newValue.toUpperCase(),
        COOKIE_EXPIRE_1_MONTH,
        true
      )
      const selectedLabel =
        COUNTRIES.find(c => c.value === newValue)?.label ?? newValue
      analytics('Click', {
        category: 'Country Switcher',
        label: `Country Switcher ${selectedLabel}`
      })
      const url = replaceLocale(newValue === 'gb' ? '' : `/${newValue}`)
      window.location.href = url
    }
  }

  useEffect(() => {
    const locale = getInitialLocale()
    setSelectedCountry(locale)
  }, [])

  if (!isReactClient) {
    return null
  }

  return (
    <div className={styles.CountrySwitcher}>
      <FormProvider {...methods}>
        <SelectField
          variant="reverse"
          id="selectedCountry"
          defaultValue={defaultCountry}
          list={COUNTRIES}
          selectedValue={selectedCountry}
          handleSelectOption={handleSelectCountry}
          data-testid="country-switcher"
        />
      </FormProvider>
    </div>
  )
}
